import React from 'react';
import { DefaultThumbnail } from '@assets';
import { CustomizedTypography } from '@components/Customized';
import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedQuotationItem
} from '@generated/graphql';
import {
  TableRow,
  TableCell,
  Box,
  makeStyles,
  useMediaQuery,
  useTheme,
  Theme
} from '@material-ui/core';
import { requestEnumToString } from '@utils/enumToString';
import AppConfig from '@config';
import { colorSet } from '@components/Provider';
import { EXAMPLE_DRAWING_THUMBNAIL } from '../assets';

interface PurchaseOrderPriceEntryProps {
  item: CapaQuotationDetailedQuotationItem;
  index: number;
  isDesign: boolean;
  isExample?: boolean;
}

const PurchaseOrderPriceEntry: React.FC<PurchaseOrderPriceEntryProps> = ({
  item,
  index,
  isDesign,
  isExample = false
}) => {
  const { tableRow } = useDocumentsPriceEntryStyles({ isDesign });
  const thumbnailUrl = isExample
    ? EXAMPLE_DRAWING_THUMBNAIL
    : `${AppConfig.fileApiUrl}/cloud/${item.fileId}.png`;
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));

  return (
    <TableRow className={tableRow}>
      <TableCell>
        <Box className="left">
          <CustomizedTypography variant="caption" className="gray600">
            {index + 1}
          </CustomizedTypography>
          {!isDesign && (
            <img
              src={thumbnailUrl}
              alt={item.fileName as string}
              onError={(e) => (e.currentTarget.src = DefaultThumbnail)}
            />
          )}

          <Box className="nameAndMaterial">
            <Box width={matches ? 170 : 450}>
              <CustomizedTypography variant="body1" color="textPrimary" noWrap>
                {item.fileName}
              </CustomizedTypography>
            </Box>
            {!isDesign && (
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                {requestEnumToString(
                  item.material as CapaQuotationDetailedMaterialType
                )}
              </CustomizedTypography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box className="right">
          {!isDesign && (
            <>
              <CustomizedTypography variant="subtitle2" color="textPrimary">
                {Math.floor(
                  Number(item.cost) / Number(item.quantity)
                ).toLocaleString('kr')}
                원
              </CustomizedTypography>
              <CustomizedTypography
                variant="subtitle2"
                color="textPrimary"
                gutterBottoms={7}
              >
                {Number(item.quantity).toLocaleString('kr')}개
              </CustomizedTypography>
            </>
          )}
          <CustomizedTypography
            variant="body1"
            color="textPrimary"
            fontWeight="500"
            align="right"
            style={{ wordBreak: 'break-all' }}
          >
            총 {Number(item.cost).toLocaleString('kr')}원
          </CustomizedTypography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default PurchaseOrderPriceEntry;

const useDocumentsPriceEntryStyles = makeStyles<Theme, { isDesign: boolean }>(
  (theme) => ({
    tableRow: {
      '& .MuiTableCell-body': {
        padding: '20px 0px',
        verticalAlign: (props) => (props.isDesign ? 'center' : 'top'),
        borderColor: colorSet.gray200
      },
      '& .left': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '& img': {
          width: 46,
          height: 46,
          border: `1px solid ${colorSet.gray200}`,
          borderRadius: 8
        },
        '& .nameAndMaterial': {
          display: 'flex',
          flexDirection: 'column',
          gap: '2px'
        }
      },
      '& .right': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '3px'
      },
      '& .gray600': {
        color: colorSet.gray600
      }
    }
  })
);
